import { z } from "zod";

/**
 * Evidence kinds, as the Evidence board columns them: plan, diff and pull request, tests,
 * review, approval, smoke test.
 */
export const evidenceKinds = ["plan", "diff", "tests", "review", "approval", "smoke"] as const;
export const evidenceKindSchema = z.enum(evidenceKinds);
export type EvidenceKind = z.infer<typeof evidenceKindSchema>;

/**
 * The state of one evidence kind for a run: verified, not yet due, unverified, or missing.
 * Missing is returned as missing, never omitted.
 */
export const evidenceStates = ["verified", "not-yet-due", "unverified", "missing"] as const;
export const evidenceStateSchema = z.enum(evidenceStates);
export type EvidenceState = z.infer<typeof evidenceStateSchema>;

export const evidenceCellSchema = z.object({
  kind: evidenceKindSchema,
  state: evidenceStateSchema,
});
export type EvidenceCell = z.infer<typeof evidenceCellSchema>;

/**
 * `GET /api/v1/projects/:id/runs/:runId/evidence`: the matrix for one run, all six kinds
 * always present.
 */
export const evidenceMatrixSchema = z.object({
  runId: z.uuid(),
  projectId: z.uuid(),
  cells: z.array(evidenceCellSchema).length(6),
  completeCount: z.number().int().min(0).max(6),
});
export type EvidenceMatrix = z.infer<typeof evidenceMatrixSchema>;

/** Artefact types, as the artefacts table shows them. */
export const artefactTypes = [
  "plan",
  "diff",
  "pull-request",
  "test-report",
  "review-record",
  "log",
] as const;
export const artefactTypeSchema = z.enum(artefactTypes);
export type ArtefactType = z.infer<typeof artefactTypeSchema>;

/**
 * One artefact, as `GET /api/v1/projects/:id/artefacts/:id` returns it. Artefacts never
 * contain a raw prompt or a secret: logs are redacted before they are stored, so only the
 * digest, the verifier, and the retention date travel here, never content.
 */
export const artefactSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  runId: z.uuid(),
  name: z.string().min(1),
  type: artefactTypeSchema,
  producingStep: z.string().min(1),
  sizeBytes: z.number().int().min(0),
  digest: z.string().min(1),
  verifier: z.string().nullable(),
  verified: z.boolean(),
  retainUntil: z.iso.datetime(),
});
export type Artefact = z.infer<typeof artefactSchema>;

export const artefactListSchema = z.array(artefactSchema);
export type ArtefactList = z.infer<typeof artefactListSchema>;

/** One pack entry: what the run touched, with its digest, never content or secrets. */
export const evidencePackEntrySchema = z.object({
  artefactId: z.uuid(),
  name: z.string().min(1),
  type: artefactTypeSchema,
  producingStep: z.string().min(1),
  digest: z.string().min(1),
  sizeBytes: z.number().int().min(0),
  verified: z.boolean(),
  verifier: z.string().nullable(),
});
export type EvidencePackEntry = z.infer<typeof evidencePackEntrySchema>;

/** One test result in the pack, read from the run's own Test and Smoke steps. */
export const evidencePackTestSchema = z.object({
  step: z.string().min(1),
  state: z.string().min(1),
  detail: z.string().nullable(),
});
export type EvidencePackTest = z.infer<typeof evidencePackTestSchema>;

/** One approval decision in the pack, bound to the run. */
export const evidencePackApprovalSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1),
  status: z.string().min(1),
  decidedBy: z.string().nullable(),
});
export type EvidencePackApproval = z.infer<typeof evidencePackApprovalSchema>;

/** One test case result in the quality section: the case it ran, its status, and its triage verdict. */
export const evidencePackQualityResultSchema = z.object({
  testCaseKey: z.string().nullable(),
  testCaseTitle: z.string().nullable(),
  status: z.string().min(1),
  attempt: z.number().int().min(1),
  triageVerdict: z.string().nullable(),
});
export type EvidencePackQualityResult = z.infer<typeof evidencePackQualityResultSchema>;

/** One test run in the quality section, with its counts by result status and its results. */
export const evidencePackQualityRunSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  environment: z.string().min(1),
  trigger: z.string().min(1),
  status: z.string().min(1),
  counts: z.object({
    passed: z.number().int().min(0),
    failed: z.number().int().min(0),
    blocked: z.number().int().min(0),
    untested: z.number().int().min(0),
    flaky: z.number().int().min(0),
    healed: z.number().int().min(0),
    needsReview: z.number().int().min(0),
    total: z.number().int().min(0),
  }),
  results: z.array(evidencePackQualityResultSchema),
});
export type EvidencePackQualityRun = z.infer<typeof evidencePackQualityRunSchema>;

/**
 * One release gate: the threshold it checks, the value measured, and whether it is met.
 * `actual` is null when nothing was measured yet, and a gate with nothing measured is not met.
 */
export const evidencePackGateSchema = z.object({
  name: z.string().min(1),
  threshold: z.number(),
  actual: z.number().nullable(),
  met: z.boolean(),
});
export type EvidencePackGate = z.infer<typeof evidencePackGateSchema>;

/**
 * The quality section of the pack: the latest test runs of the project with their results, and
 * the release gates the project is measured against. Test names, statuses, and triage verdicts
 * travel here; failure messages and logs never do.
 */
export const evidencePackQualitySchema = z.object({
  runs: z.array(evidencePackQualityRunSchema),
  gates: z.array(evidencePackGateSchema),
});
export type EvidencePackQuality = z.infer<typeof evidencePackQualitySchema>;

/**
 * The signed evidence pack for one run: artefact digests, test results, approval records, and
 * quality results and gates, with an HMAC-SHA256 signature over the canonical body. `signed` is
 * false and `signature` is null when the server holds no pack key; the pack then reports itself
 * as unsigned rather than claiming verification it cannot offer. `quality` is absent on packs
 * exported before quality results were added, and their signatures cover the body without it.
 */
export const evidencePackSchema = z.object({
  runId: z.uuid(),
  projectId: z.uuid(),
  runState: z.string().min(1),
  exportedAt: z.iso.datetime(),
  entries: z.array(evidencePackEntrySchema),
  tests: z.array(evidencePackTestSchema),
  approvals: z.array(evidencePackApprovalSchema),
  quality: evidencePackQualitySchema.optional(),
  signed: z.boolean(),
  signature: z.string().nullable(),
});
export type EvidencePack = z.infer<typeof evidencePackSchema>;

/** The verify answer: whether the pack signature checks out, and why not when it does not. */
export const evidencePackVerificationSchema = z.object({
  valid: z.boolean(),
  reason: z.string().min(1),
});
export type EvidencePackVerification = z.infer<typeof evidencePackVerificationSchema>;
